import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { DATE_INPUT_LANG } from "@/constants/dates";
import { usePreviewInvoiceSync, useRunInvoiceSync } from "@/hooks/useInvoiceSync";
import type {
  InvoiceSyncGroup,
  InvoiceSyncItem,
  InvoiceSyncReason,
  InvoiceSyncReport,
} from "@/interfaces/invoiceSync";
import { businessToday, formatDate } from "@/utils/date-format";

/** Why an item is left for a person rather than fixed. */
export const SYNC_REASON_LABEL: Record<InvoiceSyncReason, string> = {
  not_linked: "The organization's subscription is not linked in v2. Adopt it first.",
  before_link: "Raised before v2 started tracking this subscription.",
  upgrade_proration: "An upgrade proration. Upgrades are not handled in v2 yet.",
  raised_outside_v2: "Raised in Stripe by hand and written off there. Nothing to import.",
  other_billing_reason:
    "A subscription invoice v2 does not book automatically (raised by hand on the subscription, or a usage threshold).",
  void_in_v2_open_in_stripe:
    "Voided in v2 but still open in Stripe, so the client can pay it. Void it in Stripe.",
  missing_in_stripe: "Stripe no longer has this invoice (a draft deleted in the Dashboard).",
};

/** Each group's heading and what it means, in display order. */
const GROUPS: { key: InvoiceSyncGroup; title: string; explain: string }[] = [
  {
    key: "missing",
    title: "Invoices in Stripe but missing from v2",
    explain:
      "Stripe raised these for a v2 subscription and v2 never recorded them. Fixing records the invoice and any payment, and spreads the revenue over the term.",
  },
  {
    key: "payment",
    title: "Payments received at Stripe but not recorded in v2",
    explain:
      "The client paid at Stripe and v2 didn't hear about it. Fixing records the payment; a credit invoice paid in full has its credits minted.",
  },
  {
    key: "purchase",
    title: "Card purchases charged but never recorded",
    explain:
      "Stripe charged the card but v2 never completed the purchase, so no credits were added. Fixing completes it: credits added, books updated, confirmation email sent.",
  },
  {
    key: "payout",
    title: "Money that reached the bank but isn't booked",
    explain:
      "Stripe has paid these out to GFG's bank, but v2 still shows the money as sitting in Stripe. Fixing records each deposit on the day it arrived and books Stripe's fee.",
  },
  {
    key: "status",
    title: "Invoices v2 is behind on",
    explain:
      "Stripe issued, voided or wrote these off, and v2 still shows the old state. Fixing brings v2 up to date.",
  },
  {
    key: "failed_purchase",
    title: "Card purchases that failed",
    explain:
      "Stripe declined or cancelled the charge and v2 still shows the purchase in progress. Fixing marks it failed; no credits are involved.",
  },
  {
    key: "import",
    title: "Invoices raised by hand in Stripe",
    explain:
      "Raised in the Stripe Dashboard for a v2 client, so v2 has no record of them. Importing creates the invoice in the group you pick, with credits at the system rate; if Stripe has the payment, the credits are minted.",
  },
  {
    key: "person",
    title: "Left for a person",
    explain: "The sync never fixes these on its own. Each says why.",
  },
];

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monthStart = () => {
  const d = businessToday();
  return ymd(new Date(d.getFullYear(), d.getMonth(), 1, 12));
};

const money = (v: string) =>
  Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const KIND_LABEL = {
  subscription: "subscription",
  credit: "credit invoice",
  card_purchase: "card purchase",
} as const;

/** What a person has ticked: fixes and imports by Stripe id, and the group chosen for each import. */
interface Choice {
  ticked: Set<string>;
  groups: Record<string, number | null>;
}

/** Everything the preview can fix or import, ticked; imports preselect the customer's own group. */
function initialChoice(report: InvoiceSyncReport): Choice {
  const ticked = new Set<string>();
  const groups: Record<string, number | null> = {};
  for (const i of report.items) {
    if (i.action === "report") continue;
    ticked.add(i.stripeInvoiceId);
    if (i.action === "import") groups[i.stripeInvoiceId] = i.groupId;
  }
  return { ticked, groups };
}

const secondary = { color: "var(--color-graphite)" };

/** One group's items; with `choice`, each row can be ticked and an import's group picked. */
export function SyncItemsTable({
  items,
  choice,
  onChoice,
}: {
  items: InvoiceSyncItem[];
  choice?: Choice;
  onChoice?: (next: Choice) => void;
}) {
  const selectable = choice && onChoice && items.some((i) => i.action !== "report");
  const allTicked = selectable && items.every((i) => choice.ticked.has(i.stripeInvoiceId));
  const someTicked = selectable && items.some((i) => choice.ticked.has(i.stripeInvoiceId));
  const toggle = (ids: string[], on: boolean) => {
    if (!choice || !onChoice) return;
    const ticked = new Set(choice.ticked);
    for (const id of ids) {
      if (on) ticked.add(id);
      else ticked.delete(id);
    }
    onChoice({ ...choice, ticked });
  };
  const pickGroup = (id: string, groupId: number) => {
    if (!choice || !onChoice) return;
    onChoice({ ...choice, groups: { ...choice.groups, [id]: groupId } });
  };
  const forAPerson = items.every((i) => i.action === "report");
  const groupName = (i: InvoiceSyncItem) =>
    i.groupOptions?.find((g) => g.id === i.groupId)?.name ?? null;

  return (
    <Table size="small" sx={{ "& td, & th": { fontSize: 12.5, px: 1, verticalAlign: "top" } }}>
      <TableHead>
        <TableRow>
          {selectable && (
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={!!allTicked}
                indeterminate={!allTicked && !!someTicked}
                onChange={(e) =>
                  toggle(
                    items.map((i) => i.stripeInvoiceId),
                    e.target.checked,
                  )
                }
                inputProps={{ "aria-label": "Select all" }}
              />
            </TableCell>
          )}
          <TableCell>Item</TableCell>
          <TableCell>Organization</TableCell>
          <TableCell align="right">Amount</TableCell>
          <TableCell>In Stripe</TableCell>
          <TableCell>In v2</TableCell>
          <TableCell>
            {forAPerson ? "Why" : items.some((i) => i.outcome) ? "Result" : "The fix records"}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((i) => {
          const id = i.stripeInvoiceId;
          const ticked = !!choice?.ticked.has(id);
          return (
            <TableRow key={`${i.kind}-${id}`}>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={ticked}
                    onChange={(e) => toggle([id], e.target.checked)}
                    inputProps={{ "aria-label": `Select ${i.stripeInvoiceNumber ?? id}` }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Box sx={{ fontWeight: 600 }}>{i.stripeInvoiceNumber ?? id}</Box>
                <Box sx={{ ...secondary, fontSize: 11.5 }}>
                  {KIND_LABEL[i.kind]} · {formatDate(i.createdAt)}
                </Box>
              </TableCell>
              <TableCell>{i.organizationName ?? "—"}</TableCell>
              <TableCell align="right">{money(i.amountDue)}</TableCell>
              <TableCell>{i.inStripe}</TableCell>
              <TableCell>{i.inV2}</TableCell>
              <TableCell>
                {i.action === "import" && selectable ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <TextField
                      select
                      size="small"
                      label="Group"
                      value={choice.groups[id] ?? ""}
                      onChange={(e) => pickGroup(id, Number(e.target.value))}
                      disabled={!ticked}
                      error={ticked && choice.groups[id] == null}
                      sx={{ minWidth: 180, "& .MuiInputBase-root": { fontSize: 12.5 } }}
                    >
                      {(i.groupOptions ?? []).map((g) => (
                        <MenuItem key={g.id} value={g.id} sx={{ fontSize: 13 }}>
                          {g.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Box sx={{ ...secondary, fontSize: 11.5 }}>{i.fixRecords}</Box>
                  </Box>
                ) : i.action === "report" ? (
                  i.reason ? (
                    SYNC_REASON_LABEL[i.reason]
                  ) : (
                    "—"
                  )
                ) : i.action === "import" && i.outcome === "done" && groupName(i) ? (
                  `Imported into ${groupName(i)}`
                ) : (
                  i.fixRecords
                )}
                {i.outcome === "done" && (
                  <Box component="span" sx={{ color: "var(--color-good-sage)", fontWeight: 700 }}>
                    {" "}
                    Done
                  </Box>
                )}
                {i.outcome === "skipped" && (
                  <Box component="span" sx={secondary}>
                    {" "}
                    Skipped
                  </Box>
                )}
                {i.outcome === "failed" && (
                  <Box component="span" sx={{ color: "var(--color-error, #b3261e)" }}>
                    {" "}
                    Failed: {i.error}
                  </Box>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

const plural = (n: number) => (n === 1 ? "" : "s");

/** "7 already match v2, 6 need fixing, 13 need a person" — the run's outcome in finance's words. */
function Summary({ report }: { report: InvoiceSyncReport }) {
  const c = report.counts;
  const skipped = report.items.filter((i) => i.outcome === "skipped").length;
  const parts = report.dryRun
    ? [
        `${c.inStep} already match v2`,
        c.toFix ? `${c.toFix} need${c.toFix === 1 ? "s" : ""} fixing` : null,
        c.toImport ? `${c.toImport} can be imported` : null,
        c.reported ? `${c.reported} need${c.reported === 1 ? "s" : ""} a person` : null,
      ]
    : [
        `${c.inStep} already match v2`,
        c.fixed ? `${c.fixed} fixed` : null,
        c.imported ? `${c.imported} imported` : null,
        skipped ? `${skipped} skipped` : null,
        c.failed ? `${c.failed} failed` : null,
        c.reported ? `${c.reported} need${c.reported === 1 ? "s" : ""} a person` : null,
      ];
  return (
    <Box>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
        Checked {c.checked} item{plural(c.checked)} in Stripe: {parts.filter(Boolean).join(", ")}.
      </Typography>
      {c.notOurs > 0 && (
        <Typography sx={{ fontSize: 12.5, ...secondary }}>
          {c.notOurs} more belong to Stripe customers outside v2 and were left alone.
        </Typography>
      )}
    </Box>
  );
}

function ReportView({
  report,
  choice,
  onChoice,
}: {
  report: InvoiceSyncReport;
  choice?: Choice;
  onChoice?: (next: Choice) => void;
}) {
  const sections = GROUPS.map((g) => ({
    ...g,
    items: report.items.filter((i) => i.group === g.key),
  })).filter((g) => g.items.length > 0);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Summary report={report} />
      {sections.map((g) => (
        <Box key={g.key}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            {g.title} ({g.items.length})
          </Typography>
          <Typography sx={{ fontSize: 12.5, ...secondary, mb: 0.75 }}>{g.explain}</Typography>
          <SyncItemsTable items={g.items} choice={choice} onChoice={onChoice} />
        </Box>
      ))}
      {sections.length === 0 && (
        <Alert severity="success">Everything in the range matches Stripe.</Alert>
      )}
    </Box>
  );
}

/**
 * Sync with Stripe over a date range: Preview shows what would change, the person unticks anything
 * they want left and picks a group for each import, then Fix applies only what is ticked. Covers
 * both kinds of invoice. Unsettled v2 invoices are re-checked whatever their date.
 */
export default function InvoiceSyncDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const preview = usePreviewInvoiceSync();
  const run = useRunInvoiceSync();
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(() => ymd(businessToday()));
  const [choice, setChoice] = useState<Choice>({ ticked: new Set(), groups: {} });

  const report = run.data ?? preview.data;
  const busy = preview.isPending || run.isPending;
  const error = (run.error ?? preview.error) as Error | null;
  const previewing = !!preview.data && !run.data;
  const actionable = (preview.data?.items ?? []).filter((i) => i.action !== "report");
  const tickedItems = actionable.filter((i) => choice.ticked.has(i.stripeInvoiceId));
  const missingGroup = tickedItems.some(
    (i) => i.action === "import" && choice.groups[i.stripeInvoiceId] == null,
  );

  function reset() {
    preview.reset();
    run.reset();
  }
  function handleClose() {
    if (busy) return;
    reset();
    setFrom(monthStart());
    setTo(ymd(businessToday()));
    onClose();
  }
  function changeRange(set: (v: string) => void, v: string) {
    set(v);
    reset();
  }
  function fix() {
    run.mutate({
      from,
      to,
      selected: tickedItems.filter((i) => i.action !== "import").map((i) => i.stripeInvoiceId),
      imports: tickedItems
        .filter((i) => i.action === "import")
        .map((i) => ({
          stripeInvoiceId: i.stripeInvoiceId,
          groupId: choice.groups[i.stripeInvoiceId] as number,
        })),
    });
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontFamily: "var(--font-lato)", fontWeight: 700 }}>
        Sync with Stripe
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
          Compares every Stripe invoice and card purchase created in the range with v2, plus
          anything v2 still shows unsettled or not yet in the bank, whatever its date. Preview
          first, untick anything you want left alone; nothing changes until you fix.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(e) => changeRange(setFrom, e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { lang: DATE_INPUT_LANG } }}
            disabled={busy}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(e) => changeRange(setTo, e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { lang: DATE_INPUT_LANG } }}
            disabled={busy}
          />
        </Box>
        {error && <Alert severity="error">{error.message}</Alert>}
        {report && (
          <ReportView
            report={report}
            choice={previewing ? choice : undefined}
            onChoice={previewing && !busy ? setChoice : undefined}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {previewing && missingGroup && (
          <Typography sx={{ fontSize: 12.5, color: "var(--color-error, #b3261e)", mr: "auto" }}>
            Pick a group for each ticked import, or untick it.
          </Typography>
        )}
        <Button onClick={handleClose} disabled={busy} sx={{ textTransform: "none" }}>
          {run.data ? "Done" : "Cancel"}
        </Button>
        {!run.data && (
          <Button
            variant={actionable.length ? "outlined" : "contained"}
            disabled={busy || !from || !to}
            onClick={() => {
              run.reset();
              preview.mutate({ from, to }, { onSuccess: (r) => setChoice(initialChoice(r)) });
            }}
            startIcon={
              preview.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
            }
            sx={{ textTransform: "none" }}
          >
            {preview.isPending ? "Checking…" : preview.data ? "Check again" : "Preview"}
          </Button>
        )}
        {previewing && actionable.length > 0 && (
          <Button
            variant="contained"
            disabled={busy || tickedItems.length === 0 || missingGroup}
            onClick={fix}
            startIcon={run.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              textTransform: "none",
              background: "var(--color-rodeo-black)",
              "&:hover": { background: "var(--color-rodeo-black)", opacity: 0.9 },
            }}
          >
            {run.isPending
              ? "Fixing…"
              : `Fix ${tickedItems.length} invoice${plural(tickedItems.length)}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
